import { useEffect, useState } from 'react'

import { MarketPriceHistory } from '@/components/warehouse/MarketPriceHistory'
import { ApiResponseError } from '@/lib/api-fetch'
import { getYmEconomicsPiece } from '@/lib/api/client/ymarket-pricing'
import type { YmEconomicsPiece, YmEconomicsStep, YmEconomicsStepSource } from '@/lib/api/schemas/ymarket-pricing.schema'
import { formatRubleAmount } from '@/lib/market-money'
import { unavailableReasonText } from '@/lib/warehouse/labels'

const SOURCE_LABELS: Record<YmEconomicsStepSource, string> = {
  ym_offer_prices: 'цены предложений Яндекс Маркета',
  ym_promos: 'акции Яндекс Маркета',
  ym_orders: 'последний заказ Яндекс Маркета',
  ym_tariffs: 'тарифы Яндекс Маркета',
  ym_bids: 'буст Яндекс Маркета: ставка или фактическая доля кампании',
  earnings_formula: 'расчёт заработка по комиссии Маркета и закупке',
  onec_cost: 'закупка из 1С',
  ym_prices_report: 'отчёт «Цены» Яндекс Маркета',
}

// Ordered by what the owner fills first; the formula is named only when no input is missing.
const MISSING_SOURCE_NAMES: readonly (readonly [YmEconomicsStepSource, string])[] = [
  ['onec_cost', 'закупка 1С'],
  ['ym_tariffs', 'комиссия Маркета'],
  ['ym_orders', 'заказы'],
  ['ym_bids', 'буст'],
  ['ym_promos', 'акция'],
  ['ym_offer_prices', 'цены Маркета'],
  ['ym_prices_report', 'цена на витрине'],
  ['earnings_formula', 'расчёт заработка'],
]

// These reasons say the driver is absent, which is a fact rather than missing data.
const ABSENT_DRIVER_REASONS: ReadonlySet<string> = new Set(['not_in_promo', 'bids_not_set', 'not_on_display'])


const UNKNOWN_SOURCE = 'источник не указан'

function sourceText(step: YmEconomicsStep): string {
  const source = SOURCE_LABELS[step.source] ?? UNKNOWN_SOURCE
  return step.sourceDetail === null ? source : `${source} · ${step.sourceDetail}`
}

function updatedText(value: string | null): string {
  if (value === null) return 'время не записано'
  return `обновлено ${new Date(value).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' })}`
}

function missingSteps(piece: YmEconomicsPiece): YmEconomicsStep[] {
  return piece.waterfall.filter((step) => step.value === null && !ABSENT_DRIVER_REASONS.has(step.unavailableReason ?? ''))
}

/** One grey line naming the unknown inputs; the per-step reasons stay in its tooltip. */
function MissingLine({ steps }: { steps: readonly YmEconomicsStep[] }) {
  const sources = new Set(steps.map((step) => step.source))
  const inputs = MISSING_SOURCE_NAMES.filter(([source]) => source !== 'earnings_formula' && sources.has(source))
  const names = (inputs.length > 0 ? inputs : MISSING_SOURCE_NAMES.filter(([source]) => sources.has(source))).map(([, name]) => name)
  if (names.length === 0) return null
  const reasons = steps.map((step) => `${step.label}: ${step.unavailableReason === null ? 'нет данных' : unavailableReasonText(step.unavailableReason)}`).join('\n')
  return <p data-testid="market-economics-missing" title={reasons} className="mt-2 text-[11px] text-ink-muted">Нет данных: {names.join(', ')}</p>
}

type Load =
  | { kind: 'loading' }
  | { kind: 'ready'; piece: YmEconomicsPiece }
  | { kind: 'absent' }
  | { kind: 'failed' }

/** The Market's waterfall and the offer history, each open on demand; the prices live in «Цены». */
export function MarketEconomicsSection({ productId, economics }: { productId: string; economics: YmEconomicsPiece | null | undefined }) {
  const known = economics ?? null
  const [load, setLoad] = useState<Load>(known === null ? { kind: 'loading' } : { kind: 'ready', piece: known })

  useEffect(() => {
    if (known !== null) {
      setLoad({ kind: 'ready', piece: known })
      return undefined
    }
    const controller = new AbortController()
    setLoad({ kind: 'loading' })
    getYmEconomicsPiece(productId, controller.signal)
      .then((piece) => { if (!controller.signal.aborted) setLoad({ kind: 'ready', piece }) })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setLoad(cause instanceof ApiResponseError && cause.status === 404 ? { kind: 'absent' } : { kind: 'failed' })
      })
    return () => controller.abort()
  }, [known, productId])

  if (load.kind === 'absent') return null
  return (
    <section data-testid="market-economics-section" aria-label="Экономика Маркета" className="rounded-lg border border-rule bg-paper px-4 py-3">
      {load.kind === 'loading' ? <p role="status" className="text-xs text-ink-muted">Маркет: загружаем сохранённый снимок…</p> : null}
      {load.kind === 'failed' ? <p role="status" className="text-xs text-ink-muted">Маркет: экономика сейчас недоступна.</p> : null}
      {load.kind === 'ready' ? (
        <>
          <details data-testid="market-economics-details">
            <summary className="cursor-pointer select-none text-[11px] font-medium text-ink-muted hover:text-ink">Подробнее</summary>
            <ol className="mt-2 divide-y divide-rule rounded border border-rule text-xs" aria-label="Из чего складывается цена">
              {load.piece.waterfall.filter((step) => step.value !== null).map((step) => (
                <li key={step.key} data-step={step.key} className="px-3 py-1.5">
                  <div className="flex justify-between gap-3">
                    <span>{step.label}</span>
                    <strong className="tabular-nums">{step.value === null ? '—' : formatRubleAmount(step.value)}</strong>
                  </div>
                  <div className="text-[11px] text-ink-muted">Источник: {sourceText(step)} · {updatedText(step.updatedAt)}</div>
                </li>
              ))}
            </ol>
            <MissingLine steps={missingSteps(load.piece)} />
          </details>
          <MarketPriceHistory offerId={load.piece.offerId} />
        </>
      ) : null}
    </section>
  )
}
